/**
 * Keeps configurator surcharge lines in step with the padlocks they belong to.
 *
 * A surcharge is a companion cart line of a hidden CHF 1 unit product at
 * quantity = the surcharge in currency units. Each companion carries
 * `_config_parent`, the cart key of the padlock line it belongs to, and
 * `_config_unit`, the per-padlock surcharge. Reconciling against those two:
 *
 *   - padlock quantity changed  -> companion becomes unit * new quantity
 *   - padlock removed           -> companion removed
 *   - companion orphaned        -> removed
 *
 * Without this a customer can pay for keys they did not order, which is the
 * single worst failure available in this batch, so the reconcile runs after
 * every cart change rather than only on the paths the UI happens to use.
 *
 * Runs on any page that has a cart. Changes are made with /cart/change.js keyed
 * by line key, which is stable across the request and identifies a line by its
 * properties as well as its variant.
 */
class CartSurchargeSync extends HTMLElement {
  // Set as class fields, not in connectedCallback, so the flags are valid from
  // construction: reconcile() must behave correctly however it is reached.
  busy = false;
  pending = false;

  connectedCallback() {
    this.onCartUpdate = this.reconcile.bind(this);

    document.addEventListener("cart:item-quantity-update", this.onCartUpdate);
    document.addEventListener("cart:surcharge-added", this.onCartUpdate);
    document.addEventListener("cart:item-add", this.onCartUpdate);
    // Athens dispatches item-remove, not quantity-update, when a line goes to zero; without this
    // a removed padlock left its options in the cart until the next add (Sam, 2026-09-22).
    document.addEventListener("cart:item-remove", this.onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener("cart:item-quantity-update", this.onCartUpdate);
    document.removeEventListener("cart:surcharge-added", this.onCartUpdate);
    document.removeEventListener("cart:item-add", this.onCartUpdate);
    document.removeEventListener("cart:item-remove", this.onCartUpdate);
  }

  async reconcile() {
    // One reconcile at a time, because each correction re-fires the event that
    // triggers it. An event arriving mid-flight must not be dropped, though: the
    // in-flight pass may have read the cart before that change landed, and
    // silently skipping it leaves a stale surcharge on the customer's bill. So
    // note that more work arrived and run again at the end.
    if (this.busy) {
      this.pending = true;
      return;
    }

    this.busy = true;
    let settled = false;

    try {
      // Iterative rather than recursive, with a hard cap. Every pass either
      // corrects one line and re-reads, or finds nothing to do and stops. The
      // cap means a cart that somehow never converges degrades to a stale
      // surcharge rather than an infinite request loop against the shop.
      for (let pass = 0; pass < CartSurchargeSync.MAX_PASSES; pass += 1) {
        const cart = await fetch(`${window.routes.cart_url}.js`, {
          headers: { Accept: "application/json" },
        }).then((r) => r.json());

        const parents = new Map();
        cart.items.forEach((item) => parents.set(item.key, item));

        const stale = cart.items.find((item) => {
          const parentKey = item.properties?._config_parent;
          if (!parentKey) return false;
          return item.quantity !== this.wantedQuantity(item, parents);
        });

        // Nothing left to correct. `break`, not `return`: a return here would
        // skip the pending check below and drop an event that arrived while
        // this pass was reading the cart.
        if (!stale) {
          settled = true;
          break;
        }

        await this.setQuantity(stale.key, this.wantedQuantity(stale, parents));
      }

      if (!settled) console.warn("surcharge sync: gave up after too many passes");
    } catch (e) {
      console.error("surcharge sync:", e);
    } finally {
      this.busy = false;
    }

    // Something changed while we were working; the pass we just finished may
    // have missed it.
    if (this.pending) {
      this.pending = false;
      await this.reconcile();
    }
  }

  /**
   * What a surcharge line should be: its per-padlock unit times the padlock's
   * quantity, or zero when the padlock is gone, which removes the line.
   */
  wantedQuantity(item, parents) {
    const parent = parents.get(item.properties?._config_parent);
    const unit = parseInt(item.properties?._config_unit, 10) || 0;
    return parent && unit > 0 ? unit * parent.quantity : 0;
  }

  setQuantity(key, quantity) {
    return fetch(`${window.routes.cart_change_url}.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity }),
    });
  }
}

CartSurchargeSync.MAX_PASSES = 20;

customElements.define("cart-surcharge-sync", CartSurchargeSync);
